import { useEffect, useState, type ReactElement, type SubmitEvent } from "react";
import { useParams } from "react-router";

import { isLocalPath } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { useGo } from "../../app/use-go.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";
import { focusField, useFieldCheck } from "../../forms/use-field-check.js";
import { contractorRules, tenantEditRules } from "../../forms/view-model-rules.js";

import { ValueField } from "./contract-fields.js";
import { ContractorFields } from "./contractor-fields.js";
import { LoadingPage } from "./loading-page.js";
import { tenantApi, tenantUrls } from "./urls.js";
import { useSend } from "./use-send.js";

/** 入力画面を開くのに要るもの（TenantEditViewModel）。 */
export interface TenantEdit {
    readonly tenantId: number;
    readonly company: string;
    readonly userName: string;
    readonly title: string;
    readonly planDisplayName: string;
    /**
     * 有償プランの契約か（請求が発生する）。あれば、ご契約者の連絡先を部署名のほか必須にして送る前に見る。
     * <b>無い版の API もあるので省略できる</b>（そのときは任意扱いで、断るのは API。断りは欄の下に出る）。
     */
    readonly hasBilling?: boolean;
    /**
     * 「プラン変更」を出すか（サーバが決める）。<b>無償利用・月額以外の契約は false</b>——会員が自分で変えられないので、
     * ボタンの代わりに運営へのお問い合わせを案内する（Implem.Portal.Backend の docs/features/08 §1）。
     */
    readonly canChangePlan: boolean;
    /** ご契約者の連絡先。<b>無い版の API もあるので省略できる</b>（空で始める）。 */
    readonly contractorDepartment?: string | null;
    readonly contractorPostalCode?: string | null;
    readonly contractorAddress?: string | null;
    readonly contractorTel?: string | null;
    readonly contractorEmail?: string | null;
}

/** 契約者でなければ、API は行き先だけを返す（管理者は利用者一覧、それ以外は契約一覧）。 */
type EditAnswer = TenantEdit | { readonly location: string };

const title = "契約情報の編集";
const fields = [
    "Company", "UserName", "Title", "ContractorDepartment", "ContractorPostalCode", "ContractorAddress", "ContractorTel",
    "ContractorEmail",
] as const;

function EditForm({ value }: { readonly value: TenantEdit }): ReactElement {
    usePageMeta(title);
    const form = useSend(tenantApi.edit(value.tenantId), fields);
    const [values, setValues] = useState<Readonly<Record<string, string>>>({
        Company: value.company,
        UserName: value.userName,
        Title: value.title,
        ContractorDepartment: value.contractorDepartment ?? "",
        ContractorPostalCode: value.contractorPostalCode ?? "",
        ContractorAddress: value.contractorAddress ?? "",
        ContractorTel: value.contractorTel ?? "",
        ContractorEmail: value.contractorEmail ?? "",
    });
    // ご契約者の連絡先は、有償プラン（請求が発生する契約）では部署名のほかが必須
    const paid = value.hasBilling === true;
    const check = useFieldCheck({ ...tenantEditRules, ...contractorRules(paid) });
    const change = (name: string, text: string): void => {
        const next = { ...values, [name]: text };
        setValues(next);
        check.recheck((key) => next[key] ?? "");
    };
    // 送る前の断りを先に出す（ViewModel の写しで見る。forms/view-model-rules.ts）
    const fieldError = (name: string): string | undefined => check.error(name) ?? form.fieldError(name);

    const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        const invalid = check.checkAll((key) => values[key] ?? "");
        if (invalid !== undefined) {
            focusField(event.currentTarget, invalid);
            return;
        }
        form.send(new URLSearchParams(values));
    };

    return (
        <div className="page max-w-2xl">
            <h1 className="page-title">{title}</h1>
            <p className="page-lead">{value.planDisplayName}でご利用中の環境です。</p>

            <div className="panel">
                <form
                    method="post" noValidate onSubmit={onSubmit}
                    onBlur={(event) => { check.leave(event.target.getAttribute("name") ?? "", (key) => values[key] ?? ""); }}
                >
                    <FormSummary messages={form.summary} />

                    <ValueField
                        label="企業名/組織名" name="Company" required autoFocus wrapperClassName="mb-4"
                        value={values["Company"] ?? ""} onChange={change} error={fieldError("Company")}
                    />
                    <ValueField
                        label="お名前" name="UserName" required wrapperClassName="mb-4"
                        value={values["UserName"] ?? ""} onChange={change} error={fieldError("UserName")}
                    />
                    <ValueField
                        label="システム名" name="Title" required wrapperClassName="mb-4" placeholder="社内業務システム"
                        help="環境の表示名です。プリザンター側にも反映されます。"
                        value={values["Title"] ?? ""} onChange={change} error={fieldError("Title")}
                    />
                    <div className="mb-4">
                        <ContractorFields
                            values={values} required={paid} fieldError={fieldError} onChange={change}
                        />
                    </div>

                    <div className="mt-7 flex gap-3">
                        <a href={tenantUrls.list} className="btn-secondary">キャンセル</a>
                        <button type="submit" className="btn-primary grow" disabled={form.busy}>変更する</button>
                    </div>
                </form>

                {/* 操作の入口は文中のリンクにせず、短い説明とボタンを 1 行ずつ並べる（docs/01） */}
                <div className="panel-footer space-y-3">
                    {value.canChangePlan
                        ? (
                            <div className="flex items-center justify-between gap-4">
                                <p>プラン・利用人数・オプションを変える</p>
                                <a href={tenantUrls.changePlan(value.tenantId)} className="btn-secondary btn-sm shrink-0">プラン変更</a>
                            </div>
                        )
                        : (
                            // 無償利用・月額以外の契約は会員が自分で変えられない（サーバが決める）
                            <div className="flex items-center justify-between gap-4" data-plan-change-contact="">
                                <p>プランの変更は運営にお問い合わせください。</p>
                                <a href="/Home/Contact" className="btn-secondary btn-sm shrink-0">お問い合わせ</a>
                            </div>
                        )}
                    <div className="flex items-center justify-between gap-4">
                        <p>ご契約者を別の方に引き継ぐ</p>
                        <a href={tenantUrls.contractor(value.tenantId)} className="btn-secondary btn-sm shrink-0">ご契約者の変更</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** 行き先へ移る（契約者でない人が開いたとき）。 */
function Elsewhere({ location }: { readonly location: string }): ReactElement {
    const go = useGo();
    useEffect(() => {
        go(isLocalPath(location) ? location : tenantUrls.list);
    }, [go, location]);

    return <LoadingPage title={title} data={{ state: "loading" }} width="max-w-2xl" />;
}

/** 契約情報（企業名・お名前・システム名・ご契約者の連絡先）の編集。接続を許可する IP アドレスは別の画面（ip-restriction-page.tsx）。 */
export function EditPage(): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<EditAnswer>(tenantApi.edit(tenantId));

    if (data.state !== "ready") {
        return <LoadingPage title={title} data={data} width="max-w-2xl" />;
    }
    return "location" in data.value
        ? <Elsewhere location={data.value.location} />
        : <EditForm value={data.value} />;
}
